import { useRef, useState } from "react";
import { toast } from "sonner";
import { Image as ImageIcon, Upload, Trash2 } from "lucide-react";
import SectionCard from "./SectionCard";
import { editTimeLeft } from "../../lib/labels";
import { api, photoUrl, uploadFiles, formatApiError } from "../../api";
import { PHOTO_CATEGORIES } from "../../lib/labels";

export default function PhotosSection({ vehicle, onSaved }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const gallery = vehicle.photo_gallery || [];
  const locked = (vehicle.locked_sections || []).includes("photo_gallery");
  const galleryStamp = (vehicle.section_locks || {}).photo_gallery;
  const galleryTimeLeft = !locked && galleryStamp ? editTimeLeft(galleryStamp) : null;

  const saveGallery = async (g, msg) => {
    try {
      const { data } = await api.put(`/my-vehicles/${vehicle.id}/section/photo_gallery`, { value: g });
      onSaved(data);
      if (msg) toast.success(msg);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onUpload = async (files) => {
    setUploading(true);
    try {
      const paths = await uploadFiles(files);
      const newId = () => (crypto.randomUUID ? crypto.randomUUID() : `g-${Date.now()}-${Math.random().toString(36).slice(2)}`);
      const items = paths.map((p) => ({ id: newId(), path: p, category: "diger" }));
      await saveGallery([...gallery, ...items], `${items.length} fotoğraf eklendi`);
    } catch (e) { toast.error(formatApiError(e)); }
    setUploading(false);
  };

  return (
    <SectionCard testid="section-fotograflar" title="Fotoğraflar" Icon={ImageIcon}
      desc="Araç fotoğraflarını kategorilere ayırarak yükleyin; eklenen fotoğraflar 24 saat sonra kilitlenir ve değiştirilemez." hideActions>
      {locked && (
        <p data-testid="section-fotograflar-locked" className="mb-3 text-[11px] font-semibold text-amber-300/90 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2">
          Bu bölüm 24 saatlik düzenleme süresi dolduğu için kilitlendi; fotoğraflar değiştirilemez veya silinemez.
        </p>
      )}
      {galleryTimeLeft && (
        <p data-testid="section-fotograflar-countdown" className="mb-3 text-[11px] font-semibold text-cyan-300/90 rounded-lg border border-cyan-400/30 bg-cyan-400/5 px-3 py-2">
          Düzenleme süresi: {galleryTimeLeft} kaldı — süre dolunca bu bölüm kalıcı olarak kilitlenir.
        </p>
      )}
      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
        onChange={(e) => { onUpload([...e.target.files]); e.target.value = ""; }} />
      {!locked && (
      <button data-testid="fotograflar-upload-button" onClick={() => fileRef.current?.click()} disabled={uploading}
        className="flex items-center justify-center gap-2 w-full px-4 py-3.5 rounded-xl border border-dashed border-white/20 text-slate-300 text-sm hover:border-cyan-400/50 hover:text-cyan-300 transition-colors disabled:opacity-50">
        <Upload className="w-4 h-4" /> {uploading ? "Yükleniyor..." : "Fotoğraf Yükle"}
      </button>
      )}

      {gallery.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500">Henüz fotoğraf eklenmedi.</p>
      ) : (
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {gallery.map((g) => (
            <div key={g.id} data-testid={`gallery-item-${g.id}`} className="rounded-xl border border-white/10 bg-slate-950/50 overflow-hidden">
              <img src={photoUrl(g.path)} alt="" className="w-full h-28 object-cover" />
              <div className="p-2 flex items-center gap-2">
                <select data-testid={`gallery-category-${g.id}`} value={g.category}
                  onChange={(e) => saveGallery(gallery.map((x) => (x.id === g.id ? { ...x, category: e.target.value } : x)))}
                  className="flex-1 min-w-0 text-xs px-2 py-1.5 rounded-lg bg-slate-900 border border-white/10 text-slate-200 focus:outline-none focus:border-cyan-400/60">
                  {PHOTO_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <button data-testid={`gallery-delete-${g.id}`}
                  onClick={() => saveGallery(gallery.filter((x) => x.id !== g.id), "Fotoğraf silindi")}
                  disabled={locked}
                  className={`p-1.5 rounded-lg border border-white/10 text-slate-400 transition-colors shrink-0 ${locked ? "opacity-30 cursor-not-allowed" : "hover:text-red-300 hover:border-red-400/40"}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}
